import { forwardRef, useCallback, useMemo } from 'react'
import * as React from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import { INTL_DAY_MONTH_NAME_SHORT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import type { FareClassWithReward } from 'constants/flight'
import noop from 'lib/function/noop'

const SizedFilterSelectBox = styled(OfferFilterDropdownBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  checkInDate?: string
  nights: number
  active: boolean
  charterBundleRoute?: App.CharterBundleRoute
  rooms: Array<OfferPageRoom>
  fareClass: FareClassWithReward
  feederOriginAirportCode?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const CharterFlightCalendarSelectBox = forwardRef<HTMLButtonElement | HTMLDivElement, Props>((props, ref) => {
  const {
    checkInDate,
    nights,
    active,
    charterBundleRoute,
    rooms,
    fareClass,
    feederOriginAirportCode,
    onClick = noop,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const { departDate: charterDepartDate, returnDate: charterReturnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute,
    rooms,
    fareClass,
    feederOriginAirportCode,
  })

  const value = useMemo(() => {
    if (!checkInDate) {
      return undefined
    }

    const displayDepartDate = charterDepartDate ?? checkInDate
    const displayReturnDate = charterReturnDate ?? moment(checkInDate).add(nights, 'days').format(ISO_DATE_FORMAT)
    const dateText = `${intlDateFormatter(displayDepartDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)} - ${intlDateFormatter(displayReturnDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)}`
    const nightsText = intl.formatMessage(
      {
        id: 'J4HtvSjuZXgnKRv8QYUs',
        defaultMessage: '({count, plural, one {# night} other {# nights}})',
        description: 'Parenthesized night count for date selection display',
      },
      { count: nights },
    )

    return <><b>{dateText}</b> {nightsText}</>
  }, [checkInDate, charterDepartDate, charterReturnDate, intl, nights])

  const onSelectClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (active) {
      dispatch({ type: OfferPageActions.closeFilter })
    } else {
      dispatch({ type: OfferPageActions.setRoomType, room: undefined })
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      onClick(e)
    }
  }, [active, dispatch, onClick])

  return (
    <SizedFilterSelectBox
      ref={ref}
      onClick={onSelectClick}
      active={active}
      icon={<LineCalendarIcon />}
      data-testid="OfferCalendarDatePickerSelectBox"
      label={intl.formatMessage({
        id: 'W_7jAdEEow5p087vogV1',
        defaultMessage: 'When',
        description: 'Label for the date picker filter input in the search and filtering interface',
      })}
      placeholder={intl.formatMessage({
        id: '_RtJ_7VnidGvwbMWyK1Z',
        defaultMessage: 'Select your dates',
        description: 'Placeholder text for date picker filter in the search or listing filters panel',
      })}
      value={value}
    />
  )
})

export default CharterFlightCalendarSelectBox
